import { z } from 'zod';
import {
  categoriesInGroup,
  createCategoryGuard,
  isInAnyGroup,
} from './signal-category.helpers';
import {
  SignalCategoryGroupSchema,
  SignalCategorySchema,
  type SignalCategoryType,
} from './signal-category.registry';

const Category = SignalCategorySchema.enum;
const Group = SignalCategoryGroupSchema.enum;

// ─── Internal network ────────────────────────────────────────────────────────

export const INTERNAL_NETWORK_SIGNAL_CATEGORIES = categoriesInGroup(
  Group.INTERNAL_NETWORK,
);

export type InternalNetworkSignalCategoryType =
  (typeof INTERNAL_NETWORK_SIGNAL_CATEGORIES)[number];

export const isInternalNetworkSignalCategory = createCategoryGuard(
  INTERNAL_NETWORK_SIGNAL_CATEGORIES,
);

// ─── External network ────────────────────────────────────────────────────────

export const EXTERNAL_NETWORK_SIGNAL_CATEGORIES = categoriesInGroup(
  Group.EXTERNAL_NETWORK,
);

export type ExternalNetworkSignalCategoryType =
  (typeof EXTERNAL_NETWORK_SIGNAL_CATEGORIES)[number];

export const EXTERNAL_NETWORK_ONLY_CATEGORIES = [
  Category.EXPOSED_SERVICE,
  Category.EMAIL_SECURITY,
  Category.DEFAULT_CREDENTIALS,
  Category.EXPOSED_ADMIN_PANEL,
  Category.SUBDOMAIN_TAKEOVER,
] as const satisfies readonly ExternalNetworkSignalCategoryType[];

export const SURFACE_SIGNAL_CATEGORIES = [
  ...EXTERNAL_NETWORK_ONLY_CATEGORIES,
  Category.CVE,
  Category.SERVER_MISCONFIGURATION,
  Category.WEAK_CRYPTO,
  Category.INFORMATION_DISCLOSURE,
  Category.DATA_EXPOSURE,
  Category.OPEN_REDIRECT,
  Category.INSECURE_TRANSPORT,
] as const satisfies readonly ExternalNetworkSignalCategoryType[];

export const isSurfaceCategory = createCategoryGuard(SURFACE_SIGNAL_CATEGORIES);

export const TEMP_SURFACE_SIGNAL_CATEGORIES = [
  Category.CVE,
  Category.DEFAULT_CREDENTIALS,
  Category.INSECURE_TRANSPORT,
  Category.INFORMATION_DISCLOSURE,
  Category.SERVER_MISCONFIGURATION,
  Category.EXPOSED_SERVICE,
  Category.EMAIL_SECURITY,
  Category.EXPOSED_ADMIN_PANEL,
] as const satisfies readonly ExternalNetworkSignalCategoryType[];

export const EXTERNAL_NETWORK_EXECUTION_SIGNAL_CATEGORIES = [
  Category.CVE,
  Category.EXPOSED_SERVICE,
  Category.SUBDOMAIN_TAKEOVER,
  Category.INFORMATION_DISCLOSURE,
  Category.BROKEN_ACCESS,
  Category.SSRF,
  Category.OTHER,
] as const satisfies readonly ExternalNetworkSignalCategoryType[];

export const ExternalNetworkSignalCategorySchema = z.enum(
  EXTERNAL_NETWORK_SIGNAL_CATEGORIES,
);

export const EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES = [
  ...EXTERNAL_NETWORK_SIGNAL_CATEGORIES,
  Category.VULNERABILITY_CHAINING,
] as const satisfies readonly SignalCategoryType[];

export const ExternalNetworkSignalUiCategorySchema = z.enum(
  EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES,
);

export type ExternalNetworkSignalUiCategoryType =
  (typeof EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES)[number];

export const isExternalNetworkSignalUiCategory = createCategoryGuard(
  EXTERNAL_NETWORK_SIGNAL_UI_CATEGORIES,
);

// ─── Web application ─────────────────────────────────────────────────────────

export const WebAppSignalCategorySchema = z.enum(
  categoriesInGroup(Group.WEB_APP),
);

export type WebAppSignalCategoryType = z.infer<
  typeof WebAppSignalCategorySchema
>;

export const WEB_APP_SIGNAL_CATEGORIES: WebAppSignalCategoryType[] = [
  ...WebAppSignalCategorySchema.options,
];

export const isWebAppCategory = createCategoryGuard(WEB_APP_SIGNAL_CATEGORIES);

export const HttpAnalysisSignalCategorySchema =
  WebAppSignalCategorySchema.exclude([
    Category.CVE,
    Category.VULNERABILITY_CHAINING,
  ]);

export type HttpAnalysisSignalCategoryType = z.infer<
  typeof HttpAnalysisSignalCategorySchema
>;

export const HTTP_ANALYSIS_SIGNAL_CATEGORIES: HttpAnalysisSignalCategoryType[] =
  [...HttpAnalysisSignalCategorySchema.options];

export const DeepActiveProbeSignalCategorySchema =
  HttpAnalysisSignalCategorySchema.exclude([
    Category.INSECURE_TRANSPORT,
    Category.OTHER,
    Category.PROMPT_INJECTION,
    Category.PADDING_ORACLE,
    Category.RACE_CONDITION,
  ]);

export type DeepActiveProbeSignalCategoryType = z.infer<
  typeof DeepActiveProbeSignalCategorySchema
>;

export const DEEP_ACTIVE_PROBE_SIGNAL_CATEGORIES: DeepActiveProbeSignalCategoryType[] =
  [...DeepActiveProbeSignalCategorySchema.options];

const HOST_WIDE_SIGNAL_CATEGORIES = [
  Category.CORS,
  Category.INSECURE_TRANSPORT,
  Category.SERVER_MISCONFIGURATION,
] as const satisfies readonly WebAppSignalCategoryType[];

export const isHostWideSignalCategory = createCategoryGuard(
  HOST_WIDE_SIGNAL_CATEGORIES,
);

// ─── Agentic AI ──────────────────────────────────────────────────────────────

export const AGENTIC_AI_SIGNAL_CATEGORIES = categoriesInGroup(Group.AGENTIC_AI);

export type AgenticAiSignalCategoryType =
  (typeof AGENTIC_AI_SIGNAL_CATEGORIES)[number];

export const isAgenticAiSignalCategory = createCategoryGuard(
  AGENTIC_AI_SIGNAL_CATEGORIES,
);

export const AGENTIC_AI_FLOW_SIGNAL_CATEGORIES = [
  Category.AGENT_GOAL_HIJACK,
  Category.AGENTIC_SUPPLY_CHAIN,
  Category.INSECURE_INTER_AGENT_COMMUNICATION,
  Category.CASCADING_FAILURES,
  Category.HUMAN_AGENT_TRUST_EXPLOITATION,
  Category.ROGUE_AGENTS,
] as const satisfies readonly AgenticAiSignalCategoryType[];

export type AgenticAiFlowSignalCategoryType =
  (typeof AGENTIC_AI_FLOW_SIGNAL_CATEGORIES)[number];

export const AGENTIC_AI_NODE_SIGNAL_CATEGORIES = [
  Category.TOOL_MISUSE,
  Category.IDENTITY_PRIVILEGE_ABUSE,
  Category.UNEXPECTED_CODE_EXECUTION,
  Category.MEMORY_CONTEXT_POISONING,
  Category.AI_CACHE_POISONING,
] as const satisfies readonly AgenticAiSignalCategoryType[];

export type AgenticAiNodeSignalCategoryType =
  (typeof AGENTIC_AI_NODE_SIGNAL_CATEGORIES)[number];

export const EXCHANGE_ANALYSIS_SHARED_WEB_CATEGORIES = [
  Category.PROMPT_INJECTION,
  Category.DATA_EXPOSURE,
] as const satisfies readonly WebAppSignalCategoryType[];

export const AGENTIC_AI_NODE_SIGNAL_CATEGORIES_EXTENDED = [
  ...AGENTIC_AI_NODE_SIGNAL_CATEGORIES,
  ...EXCHANGE_ANALYSIS_SHARED_WEB_CATEGORIES,
] as const satisfies readonly SignalCategoryType[];

export type AgenticAiNodeSignalCategoryTypeExtended =
  (typeof AGENTIC_AI_NODE_SIGNAL_CATEGORIES_EXTENDED)[number];

// ─── MCP server ──────────────────────────────────────────────────────────────

export const MCP_SERVER_SIGNAL_CATEGORIES = categoriesInGroup(Group.MCP_SERVER);

export type McpServerSignalCategoryType =
  (typeof MCP_SERVER_SIGNAL_CATEGORIES)[number];

export const isMcpServerSignalCategory = createCategoryGuard(
  MCP_SERVER_SIGNAL_CATEGORIES,
);

export const V2_MCP_SERVER_SIGNAL_CATEGORIES = [
  ...MCP_SERVER_SIGNAL_CATEGORIES,
] as const satisfies readonly SignalCategoryType[];

export type V2McpServerSignalCategoryType =
  (typeof V2_MCP_SERVER_SIGNAL_CATEGORIES)[number];

export const MCP_WEB_ALLOWED_CATEGORY_LIST = [
  Category.CORS,
  Category.INSECURE_TRANSPORT,
  Category.WEAK_CRYPTO,
  Category.SERVER_MISCONFIGURATION,
  Category.HTTP_REQUEST_SMUGGLING,
  Category.HTTP_RESPONSE_SPLITTING,
  Category.INFORMATION_DISCLOSURE,
  Category.IDOR,
  Category.BOLA,
  Category.BFLA,
  Category.BOPLA,
  Category.BROKEN_AUTHENTICATION,
  Category.XXE,
  Category.LDAP_INJECTION,
  Category.XPATH_INJECTION,
  Category.INSECURE_DESERIALIZATION,
] as const satisfies readonly WebAppSignalCategoryType[];

export const MCP_WEB_ALLOWED_CATEGORIES: ReadonlySet<SignalCategoryType> =
  new Set(MCP_WEB_ALLOWED_CATEGORY_LIST);

export const MCP_SERVER_SCOPE_CATEGORIES = [
  ...new Set([
    ...MCP_SERVER_SIGNAL_CATEGORIES,
    ...MCP_WEB_ALLOWED_CATEGORY_LIST,
  ]),
] as const satisfies readonly SignalCategoryType[];

export type McpServerScopeCategoryType =
  (typeof MCP_SERVER_SCOPE_CATEGORIES)[number];

// ─── AI application (web + agentic + MCP) ────────────────────────────────────

export const AI_APPLICATION_SIGNAL_CATEGORIES: SignalCategoryType[] =
  SignalCategorySchema.options.filter((category) =>
    isInAnyGroup(category, [Group.WEB_APP, Group.AGENTIC_AI, Group.MCP_SERVER]),
  );

export const isAiApplicationSignalCategory = createCategoryGuard(
  AI_APPLICATION_SIGNAL_CATEGORIES,
);
